@import "tailwindcss";
@import "./styles+.css";

/* Tailwind plugins */
@import "tw-animate-css";
@plugin "@tailwindcss/typography";

@custom-variant dark (&:is(.dark *));

body {
  @apply m-0;
  font-family:
    -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu",
    "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

code {
  font-family:
    source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace;
}

/* https://tweakcn.com/editor/theme */
/*  https://ui.shadcn.com/themes */
@theme inline {
  --radius-sm: calc(var(--radius) - 4px);
  --radius-md: calc(var(--radius) - 2px);
  --radius-lg: var(--radius);
  --radius-xl: calc(var(--radius) + 4px);
  --color-background: var(--background);
  --color-foreground: var(--foreground);
  --color-card: var(--card);
  --color-card-foreground: var(--card-foreground);
  --color-popover: var(--popover);
  --color-popover-foreground: var(--popover-foreground);
  --color-primary: var(--primary);
  --color-primary-foreground: var(--primary-foreground);
  --color-secondary: var(--secondary);
  --color-secondary-foreground: var(--secondary-foreground);
  --color-muted: var(--muted);
  --color-muted-foreground: var(--muted-foreground);
  --color-accent: var(--accent);
  --color-accent-foreground: var(--accent-foreground);
  --color-link: var(--link);
  --color-link-hover: var(--link-hover);
  --color-destructive: var(--destructive);
  --color-destructive-foreground: var(--destructive-foreground);
  --color-border: var(--border);
  --color-input: var(--input);
  --color-ring: var(--ring);
  --color-chart-1: var(--chart-1);
  --color-chart-2: var(--chart-2);
  --color-chart-3: var(--chart-3);
  --color-chart-4: var(--chart-4);
  --color-chart-5: var(--chart-5);
  --color-sidebar: var(--sidebar);
  --color-sidebar-foreground: var(--sidebar-foreground);
  --color-sidebar-primary: var(--sidebar-primary);
  --color-sidebar-primary-foreground: var(--sidebar-primary-foreground);
  --color-sidebar-accent: var(--sidebar-accent);
  --color-sidebar-accent-foreground: var(--sidebar-accent-foreground);
  --color-sidebar-border: var(--sidebar-border);
  --color-sidebar-ring: var(--sidebar-ring);
}

/* ============================================================
   Orto-Mate — colour palette
   Brand primary green #00DD7A ("live / powered / on") on warm
   neutral surfaces. Bright green fill takes dark ink, never white.
   ============================================================ */
:root {
  --radius: 0.625rem;
  --background: #f8f8f5; /* neutral-50 — faint warm app bg */
  --foreground: #1a1a17; /* neutral-900 — ink */
  --card: #ffffff; /* neutral-0  — white surface */
  --card-foreground: #1a1a17;
  --popover: #ffffff;
  --popover-foreground: #63625a; /* neutral-600 */
  --primary: #00dd7a; /* green-500 — BRAND PRIMARY */
  --primary-foreground: #1a1a17; /* dark ink on bright green */
  --secondary: #f1f1ec; /* neutral-100 — sunken surface */
  --secondary-foreground: #1a1a17;
  --muted: #f1f1ec; /* neutral-100 */
  --muted-foreground: #63625a; /* neutral-600 */
  --accent: #ecfdf4; /* green-50 — subtle brand tint */
  --accent-foreground: #0a7343; /* green-800 */
  --link: #0a7343; /* green-800 — link text, AA on white */
  --link-hover: #0c5535; /* green-900 — link hover */
  --destructive: #e5484d; /* red-500 */
  --destructive-foreground: #ffffff;
  --border: #e4e3dc; /* neutral-200 */
  --input: #e4e3dc; /* neutral-200 */
  --ring: #00dd7a; /* green-500 — focus */
  --chart-1: #00dd7a; /* green-500 */
  --chart-2: #2e7df6; /* blue-500 */
  --chart-3: #f5a524; /* amber-500 */
  --chart-4: #e5484d; /* red-500 */
  --chart-5: #009252; /* green-700 */
  --sidebar: #ffffff;
  --sidebar-foreground: #63625a; /* neutral-600 */
  --sidebar-primary: #00dd7a;
  --sidebar-primary-foreground: #1a1a17;
  --sidebar-accent: #ecfdf4; /* green-50 */
  --sidebar-accent-foreground: #0a7343;
  --sidebar-border: #e4e3dc;
  --sidebar-ring: #00dd7a;
}

.dark {
  --background: #1a1a17; /* neutral-900 */
  --foreground: #f8f8f5; /* neutral-50 */
  --card: #2e2d29; /* neutral-800 */
  --card-foreground: #f8f8f5;
  --popover: #2e2d29;
  --popover-foreground: #cfcec4; /* neutral-300 */
  --primary: #00dd7a; /* green-500 — brand stays bright */
  --primary-foreground: #0e0e0c; /* neutral-950 ink */
  --secondary: #2e2d29; /* neutral-800 */
  --secondary-foreground: #f8f8f5;
  --muted: #2e2d29; /* neutral-800 */
  --muted-foreground: #aba99d; /* neutral-400 */
  --accent: #0c5535; /* green-900 — subtle brand tint */
  --accent-foreground: #a6f4cd; /* green-200 */
  --link: #6eedad; /* green-300 — link text, AA on dark */
  --link-hover: #a6f4cd; /* green-200 — link hover */
  --destructive: #e5484d; /* red-500 */
  --destructive-foreground: #ffffff;
  --border: #494841; /* neutral-700 */
  --input: #494841; /* neutral-700 */
  --ring: #00dd7a; /* green-500 */
  --chart-1: #2ee68c; /* green-400 */
  --chart-2: #2e7df6; /* blue-500 */
  --chart-3: #f5a524; /* amber-500 */
  --chart-4: #e5484d; /* red-500 */
  --chart-5: #6eedad; /* green-300 */
  --sidebar: #0e0e0c; /* neutral-950 */
  --sidebar-foreground: #aba99d; /* neutral-400 */
  --sidebar-primary: #00dd7a;
  --sidebar-primary-foreground: #0e0e0c;
  --sidebar-accent: #0c5535; /* green-900 */
  --sidebar-accent-foreground: #a6f4cd;
  --sidebar-border: #494841;
  --sidebar-ring: #00dd7a;
}

@layer base {
  html {
    font-size: 18px;
  }
  * {
    @apply border-border outline-ring/50;
  }
  body {
    @apply bg-background text-foreground;
  }
}
